import { api, isRole, type Role } from '@/shared/api';
import { unwrap, unwrapCollection, type Page } from '@/shared/api/envelope';

import { MEMBERSHIP_STATUSES, type MembershipStatus } from './model';
import {
    platformUserResourceSchema,
    type PlatformMembershipResource,
    type PlatformUserResource,
} from './types';

/**
 * Het cross-tenant beeld van een persoon. **Alleen platformbeheer.**
 *
 * Een keurmeester kan bij meerdere keuringsbedrijven horen. Binnen een bedrijf
 * mag dat nooit zichtbaar zijn (bedrijfsgevoelig); hier mag het wél, want dit is
 * precies de vraag die het platform moet kunnen beantwoorden: wie werkt waar, in
 * welke rol, met welke status.
 */
export interface PlatformMembership {
    tenantUuid: string;
    tenantName: string;
    /** De meest verstrekkende bedrijfsrol; `null` als de API een rol stuurt die de app niet kent. */
    role: Role | null;
    status: MembershipStatus;
    joinedAt: string | null;
}

export interface PlatformUser {
    uuid: string;
    name: string;
    email: string;
    phone: string | null;
    isPlatformAdmin: boolean;
    lastLoginAt: string | null;
    createdAt: string | null;
    memberships: PlatformMembership[];
}

function toPlatformMembership(resource: PlatformMembershipResource): PlatformMembership {
    return {
        tenantUuid: resource.tenant_uuid,
        tenantName: resource.tenant_name,
        role: resource.role !== null && isRole(resource.role) ? resource.role : null,
        status: (MEMBERSHIP_STATUSES as readonly string[]).includes(resource.status)
            ? (resource.status as MembershipStatus)
            : 'disabled',
        joinedAt: resource.joined_at,
    };
}

export function toPlatformUser(resource: PlatformUserResource): PlatformUser {
    return {
        uuid: resource.uuid,
        name: resource.name,
        email: resource.email,
        phone: resource.phone,
        isPlatformAdmin: resource.is_platform_admin,
        lastLoginAt: resource.last_login_at,
        createdAt: resource.created_at,
        memberships: resource.memberships.map(toPlatformMembership),
    };
}

export interface PlatformUserFilters {
    /** Uuid van de organisatie; de filters gelden samen voor hetzelfde lidmaatschap. */
    tenantUuid?: string;
    role?: Role | '';
    status?: MembershipStatus | '';
    search?: string;
    page?: number;
    perPage?: number;
}

export const platformUserApi = {
    /** `GET /platform/users` - één rij per persoon, met al zijn lidmaatschappen. */
    async list(filters: PlatformUserFilters = {}): Promise<Page<PlatformUser>> {
        const page = unwrapCollection(
            platformUserResourceSchema,
            await api.get('/platform/users', {
                query: {
                    tenant: filters.tenantUuid || undefined,
                    role: filters.role || undefined,
                    status: filters.status || undefined,
                    search: filters.search || undefined,
                    page: filters.page,
                    per_page: filters.perPage ?? 50,
                },
            }),
        );

        return { items: page.items.map(toPlatformUser), meta: page.meta };
    },

    /** `GET /platform/users/{user}` - één persoon, op zijn uuid. */
    async find(userUuid: string): Promise<PlatformUser> {
        return toPlatformUser(
            unwrap(platformUserResourceSchema, await api.get(`/platform/users/${userUuid}`)),
        );
    },
};
